<template>
  <div>
    <!-- Header -->
    <v-row class="ma-0 my-4 align-center justify-space-between">
      <h1 class="text-h5 font-weight-bold">MRR Breakdown</h1>
      <v-col v-if="loadingError" class="mr-3 ma-0 pa-0 mt-3 mt-md-0 d-flex align-end justify-end">
        <v-alert dense outlined type="error">Ocurrió un error al cargar los datos</v-alert>
      </v-col>
    </v-row>

    <!-- Stats -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6">
        <v-card outlined class="pa-4">
          <div class="text-caption text-uppercase grey--text text--darken-1 mb-1">
            MRR oficial USD
            <span v-if="filtroAnio"> · {{ filtroAnio }}<span v-if="filtroMes">/{{ filtroMes }}</span></span>
            <v-tooltip bottom max-width="360">
              <template v-slot:activator="{ on }">
                <v-icon x-small class="ml-1" v-on="on">mdi-help-circle-outline</v-icon>
              </template>
              <span>
                El MRR del mes tal como lo cuentan MRR Resumen y MRR Daily: solo las
                líneas de negocio reconocidas por país, sin aplicar los filtros de
                esta tabla. Por eso no cambia al filtrar por estado o contrato.
              </span>
            </v-tooltip>
          </div>
          <div v-if="mrrOficialMes !== null" class="text-h5 font-weight-bold primary--text">
            {{ formatUSD(mrrOficialMes) }}
          </div>
          <div v-else class="text-h5 font-weight-bold grey--text">—</div>
          <div class="text-caption grey--text text--darken-1 mt-1">
            Suma visible con los filtros aplicados: {{ formatUSD(totalUSD) }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6">
        <v-card outlined class="pa-4">
          <div class="text-caption text-uppercase grey--text text--darken-1 mb-1">Contratos</div>
          <div class="text-h5 font-weight-bold">{{ contratosVisibles }}</div>
          <!-- Una fila no es un contrato: hay filas sin contrato (B2C agregado,
               billing sin clasificar), varias filas del mismo contrato en un mes
               (una por moneda, excedentes de EB) y las sintéticas en $0. Por eso
               se muestran las dos cifras. -->
          <div class="text-caption grey--text text--darken-1 mt-1">
            {{ filteredRows.length }} registros en la tabla
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filtros: misma card de selects que MRR Resumen y MRR New. -->
    <v-card outlined class="mb-4 pa-4">
      <v-row dense align="center">
        <v-col cols="6" sm="4" md="2">
          <v-select
            v-model="filtroAnio"
            :items="opcionesAnio"
            label="Año"
            outlined dense hide-details
          />
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-select
            v-model="filtroMes"
            :items="opcionesMes"
            label="Mes"
            :disabled="!filtroAnio"
            outlined dense hide-details
          />
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-select
            v-model="filtroPais"
            :items="opcionesPais"
            label="País"
            outlined dense hide-details
          />
        </v-col>
        <v-col cols="6" sm="6" md="3">
          <v-select
            v-model="filtroLinea"
            :items="opcionesLinea"
            label="Línea de negocio"
            outlined dense hide-details
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="filtroEstado"
            :items="opcionesEstado"
            label="Estado"
            outlined dense hide-details
          />
        </v-col>
      </v-row>

      <v-row dense align="center" class="mt-2">
        <v-col cols="12" md="5">
          <v-autocomplete
            v-model="filtroContrato"
            :items="contratosDisponibles"
            item-text="text"
            item-value="value"
            label="Contrato"
            outlined dense hide-details clearable
            prepend-inner-icon="mdi-file-document-outline"
            no-data-text="Sin contratos que coincidan"
          />
        </v-col>
        <v-spacer />
        <v-col cols="12" md="auto">
          <v-btn small text color="primary" @click="limpiarFiltros">
            Limpiar filtros
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Tabla -->
    <v-data-table
      :headers="headers"
      :items="filteredRows"
      :loading="loadingData"
      :items-per-page="50"
      sort-by="momUsd"
      class="elevation-1 mrr-breakdown-table"
      :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
      :item-class="rowClass"
    >
      <template v-slot:item.canal="{ item }">
        <span class="grey--text text--darken-2">{{ item.canal || '—' }}</span>
      </template>

      <template v-slot:item.producto="{ item }">
        <span class="grey--text text--darken-2">{{ item.producto || '—' }}</span>
        <v-tooltip v-if="item.oficial === false" bottom max-width="320">
          <template v-slot:activator="{ on }">
            <v-chip x-small outlined color="grey darken-1" class="ml-2" v-on="on">
              fuera del MRR oficial
            </v-chip>
          </template>
          <span>
            Su combinación de país y línea de negocio ({{ item.pais || 'sin país' }} ·
            {{ item.lineaNegocio || 'sin línea' }}) no está en el catálogo oficial, así
            que suma en esta tabla pero no en el MRR oficial del mes.
          </span>
        </v-tooltip>
      </template>

      <template v-slot:item.contrato="{ item }">
        <span
          class="grey--text text--darken-1 font-weight-medium"
          :class="{ 'primary--text text-decoration-underline': canOpenBreakdown(item) }"
          :style="canOpenBreakdown(item) ? 'cursor:pointer' : ''"
          @click="canOpenBreakdown(item) && openBreakdown(item)"
        >{{ item.contrato || '—' }}</span>
      </template>

      <template v-slot:item.estimado="{ item }">
        <v-chip v-if="item.estimado" x-small color="orange" dark>Forecast</v-chip>
        <v-chip v-else x-small color="green" outlined>Real</v-chip>
      </template>

      <template v-slot:item.mrrLocal="{ item }">
        {{ formatLocal(item.mrrLocal, item.moneda) }}
      </template>

      <template v-slot:item.mrrUSD="{ item }">
        {{ formatUSD(item.mrrUSD) }}
      </template>

      <template v-slot:item.totalMrrLocal="{ item }">
        <span class="font-weight-medium">{{ formatLocal(item.totalMrrLocal, item.moneda) }}</span>
      </template>

      <template v-slot:item.totalMrrUSD="{ item }">
        <span class="font-weight-medium">{{ formatUSD(item.totalMrrUSD) }}</span>
      </template>

      <template v-slot:item.momUsd="{ item }">
        <delta-badge
          v-if="varFor(item.contrato)"
          :usd="varFor(item.contrato).momUsd"
          :pct="varFor(item.contrato).momPct"
          label="Mes anterior"
        />
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:item.acciones="{ item }">
        <!-- Detalle del Total MRR: recaudación, comisión, gestión y upsell. Iban como cuatro
             columnas y obligaban a hacer scroll horizontal; ahora se despliegan por fila.
             El backend solo los calcula para el mes en curso, así que en meses pasados no
             hay detalle y el botón no aparece. -->
        <v-menu v-if="hasExtras(item)" offset-y left :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon small color="primary"
              title="Ver recaudación, comisión, gestión y upsell"
              v-bind="attrs" v-on="on"
            >
              <v-icon small>mdi-cash-plus</v-icon>
            </v-btn>
          </template>
          <v-card class="extras-detail" min-width="260">
            <v-card-text class="pa-3">
              <div class="pw-label mb-2">Detalle del Total MRR · {{ item.contrato }}</div>
              <div v-for="line in extrasDetail(item)" :key="line.label" class="extras-line">
                <span>{{ line.label }}</span>
                <span class="pw-num">{{ formatLocal(line.value, item.moneda) }}</span>
              </div>
              <v-divider class="my-2" />
              <div class="extras-line font-weight-medium">
                <span>Total MRR</span>
                <span class="pw-num">{{ formatLocal(item.totalMrrLocal, item.moneda) }}</span>
              </div>
            </v-card-text>
          </v-card>
        </v-menu>
        <!-- Sin un mes único al que anclar la serie (año o mes en "Todos") no hay
             tendencia que pedir, así que el botón queda deshabilitado y lo explica. -->
        <v-btn
          v-if="item.contrato"
          icon small color="info"
          :disabled="!filtroAnio || !filtroMes"
          :title="periodoCompleto ? 'Ver tendencia diaria' : 'Elegí un año y un mes para ver la tendencia'"
          @click="openTrend(item)"
        >
          <v-icon small>mdi-chart-line</v-icon>
        </v-btn>
        <template v-if="item.id">
          <v-btn v-if="can('mrr-breakdown:update-billing')" icon small color="primary" @click="openEdit(item)" title="Editar">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn v-if="can('mrr-breakdown:delete-billing')" icon small color="pink" @click="openDelete(item)" title="Eliminar">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </template>
        <v-btn v-else-if="item.contratoId && can('mrr-breakdown:create-billing')" icon small color="success" @click="openCreate(item)" title="Agregar registro">
          <v-icon small>mdi-plus</v-icon>
        </v-btn>
      </template>

      <template v-slot:body.append>
        <tr class="font-weight-bold grey lighten-4">
          <td colspan="6" class="pa-3 text-right">Total</td>
          <td class="pa-3">{{ formatUSD(totalUSD) }}</td>
          <!-- El Total MRR va en la moneda de cada fila, así que sumarlo entre monedas no
               significa nada: el único total agregable es el de USD. -->
          <td></td>
          <td class="pa-3">{{ formatUSD(totalMrrConExtrasUSD) }}</td>
          <td colspan="3"></td>
        </tr>
      </template>
    </v-data-table>

    <!-- Tendencia diaria del contrato -->
    <contract-mrr-trend-dialog
      v-if="trendDialog"
      v-model="trendDialog"
      :contrato="trendContrato"
      :year="filtroAnio"
      :month="filtroMes"
    />

    <!-- Edit dialog -->
    <v-dialog v-model="editDialog" max-width="480">
      <v-card class="pa-4">
        <v-card-title class="text-uppercase">Editar registro MRR</v-card-title>
        <hr class="my-3" />
        <v-card-text>
          <v-form ref="editForm" v-model="editFormValid">
            <v-row>
              <v-col cols="6">
                <v-text-field
                  label="MRR Local"
                  v-model.number="editItem.mrrLocal"
                  type="number"
                  outlined dense hide-details="auto"
                  :rules="[v => (v !== null && v !== undefined && v !== '') || 'Requerido']"
                />
              </v-col>
              <v-col cols="6">
                <v-select
                  label="Moneda"
                  v-model="editItem.moneda"
                  :items="monedas"
                  outlined dense hide-details="auto"
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  label="Mes"
                  v-model.number="editItem.mes"
                  type="number"
                  :min="1" :max="12"
                  outlined dense hide-details="auto"
                  :rules="[v => (v >= 1 && v <= 12) || 'Entre 1 y 12']"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  label="Año"
                  v-model.number="editItem.anio"
                  type="number"
                  outlined dense hide-details="auto"
                  :rules="[v => (v >= 2020 && v <= 2099) || 'Año inválido']"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  label="Notas"
                  v-model="editItem.notas"
                  outlined dense hide-details="auto"
                />
              </v-col>
            </v-row>
          </v-form>
          <v-alert v-if="editError" type="error" dense class="mt-3">
            Ocurrió un error al guardar, intenta más tarde.
          </v-alert>
        </v-card-text>
        <hr class="my-2" />
        <v-card-actions class="justify-end">
          <v-btn text color="primary" rounded @click="editDialog = false" :disabled="editLoading">Cerrar</v-btn>
          <v-btn color="primary" rounded :disabled="!editFormValid" :loading="editLoading" @click="saveEdit">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create dialog (filas sin dato ese mes) -->
    <v-dialog v-model="createDialog" max-width="480">
      <v-card class="pa-4">
        <v-card-title class="text-uppercase">Agregar registro MRR</v-card-title>
        <v-card-subtitle class="pb-0">
          {{ createItem.contrato }} — {{ createItem.empresa }} — {{ String(createItem.mes).padStart(2, '0') }}/{{ createItem.anio }}
        </v-card-subtitle>
        <hr class="my-3" />
        <v-card-text>
          <v-form ref="createForm" v-model="createFormValid">
            <v-row>
              <v-col cols="6">
                <v-text-field
                  label="MRR Local"
                  v-model.number="createItem.mrrLocal"
                  type="number"
                  outlined dense hide-details="auto"
                  :rules="[v => (v !== null && v !== undefined && v !== '') || 'Requerido']"
                />
              </v-col>
              <v-col cols="6">
                <v-select
                  label="Moneda"
                  v-model="createItem.moneda"
                  :items="monedas"
                  outlined dense hide-details="auto"
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  label="Notas"
                  v-model="createItem.notas"
                  outlined dense hide-details="auto"
                />
              </v-col>
            </v-row>
          </v-form>
          <v-alert v-if="createError" type="error" dense class="mt-3">
            Ocurrió un error al guardar, intenta más tarde.
          </v-alert>
        </v-card-text>
        <hr class="my-2" />
        <v-card-actions class="justify-end">
          <v-btn text color="primary" rounded @click="createDialog = false" :disabled="createLoading">Cerrar</v-btn>
          <v-btn color="primary" rounded :disabled="!createFormValid" :loading="createLoading" @click="saveCreate">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title>Eliminar registro</v-card-title>
        <v-card-text>
          ¿Estás seguro de que deseas eliminar este registro de MRR? Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text color="primary" rounded @click="deleteDialog = false" :disabled="deleteLoading">Cancelar</v-btn>
          <v-btn color="error" rounded :loading="deleteLoading" @click="confirmDelete">Sí, eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- B2B2C Breakdown dialog -->
    <v-dialog v-model="breakdownDialog" max-width="700">
      <v-card class="pa-4">
        <v-card-title class="text-uppercase pb-1">
          Membresías activas — {{ breakdownContrato }}
        </v-card-title>
        <v-card-subtitle class="pb-3">{{ breakdownEmpresa }}</v-card-subtitle>
        <hr class="mb-3" />
        <v-data-table
          :headers="breakdownHeaders"
          :items="breakdownRows"
          :loading="breakdownLoading"
          :items-per-page="20"
          no-data-text="No hay membresías activas"
          dense
        />
        <hr class="mt-3" />
        <v-card-actions class="justify-end">
          <v-btn text color="primary" @click="breakdownDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar error delete -->
    <v-snackbar v-model="deleteError" :timeout="5000">
      Ocurrió un error al eliminar el registro, intenta más tarde.
      <template v-slot:action="{ attrs }">
        <v-btn color="red" text v-bind="attrs" @click="deleteError = false">Entendido</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import MRRService from '../services/mrr.service';
import ContratoService from '../services/contrato.service';
import BillingService from '../services/billing.service';
import DeltaBadge from '../components/DeltaBadge.vue';
import ContractMrrTrendDialog from '../components/ContractMrrTrendDialog.vue';
import { can } from '../utils/permissions';

export default {
  name: 'MrrDetalle',
  components: { DeltaBadge, ContractMrrTrendDialog },
  data() {
    return {
      // Monedas con que se carga o edita una fila de MRR. MXN desde la spec 084.
      monedas: ['CLP', 'PEN', 'USD', 'MXN'],
      loadingData: false,
      loadingError: false,
      rows: [],
      contratos: [],
      variationsByContract: {},
      // Tendencia diaria por contrato
      trendDialog: false,
      trendContrato: '',
      filtroAnio: String(new Date().getFullYear()),
      filtroMes: String(new Date().getMonth() + 1).padStart(2, '0'),
      filtroContrato: null,
      filtroEstado: 'Activo',
      filtroPais: '',
      filtroLinea: '',
      mesesNombre: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
      headers: [
        { text: 'Fecha',     value: 'fecha',    sortable: true },
        { text: 'Contrato',  value: 'contrato', sortable: true },
        { text: 'Empresa',   value: 'empresa',  sortable: true },
        { text: 'Broker',    value: 'canal',    sortable: true },
        { text: 'Producto',  value: 'producto', sortable: true },
        { text: 'MRR Local', value: 'mrrLocal', sortable: true, align: 'right' },
        { text: 'MRR USD',   value: 'mrrUSD',   sortable: true, align: 'right' },
        { text: 'Total MRR',     value: 'totalMrrLocal', sortable: true, align: 'right' },
        { text: 'Total MRR USD', value: 'totalMrrUSD',   sortable: true, align: 'right' },
        { text: 'Δ Mes',     value: 'momUsd', sortable: true, align: 'right' },
        { text: 'Forecast',  value: 'estimado', sortable: false },
        { text: 'Acciones',  value: 'acciones', sortable: false, align: 'center' },
      ],
      // Edit dialog
      editDialog: false,
      editFormValid: false,
      editLoading: false,
      editError: false,
      editItem: { id: null, mrrLocal: 0, moneda: 'CLP', mes: 1, anio: 2025, notas: null },
      // Create dialog (filas sin fila de billing ese mes — ver filteredRows)
      createDialog: false,
      createFormValid: false,
      createLoading: false,
      createError: false,
      createItem: { contratoId: null, companyId: null, contrato: null, empresa: null, mrrLocal: 0, moneda: 'CLP', mes: 1, anio: 2025, notas: null },
      // Delete dialog
      deleteDialog: false,
      deleteLoading: false,
      deleteError: false,
      deleteTarget: null,
      // Breakdown dialog
      breakdownDialog: false,
      breakdownLoading: false,
      breakdownContrato: null,
      breakdownEmpresa: null,
      breakdownRows: [],
      breakdownHeaders: [
        { text: 'Email',   value: 'email',       sortable: true },
        { text: 'Plan',    value: 'planName',     sortable: true },
        { text: 'Empresa', value: 'companyName',  sortable: true },
      ],
    };
  },
  computed: {
    anios() {
      return [...new Set(this.rows.map(r => r.fecha?.slice(0, 4)).filter(Boolean))].sort().reverse();
    },
    opcionesAnio() {
      return [{ text: 'Todos', value: '' }, ...this.anios.map(a => ({ text: a, value: a }))];
    },
    opcionesMes() {
      return [
        { text: 'Todos', value: '' },
        ...this.mesesNombre.map((m, i) => ({
          text: m,
          value: String(i + 1).padStart(2, '0'),
        })),
      ];
    },
    opcionesPais() {
      const paises = [...new Set(this.rows.map(r => r.pais).filter(Boolean))].sort();
      return [{ text: 'Todos', value: '' }, ...paises.map(p => ({ text: p, value: p }))];
    },
    // Se filtra por la línea normalizada (Directo / Brokers), que es el
    // vocabulario de MRR Resumen y MRR Daily; la cruda los mezcla en un solo
    // valor y no coincide con lo que el usuario ve en el resto del módulo.
    opcionesLinea() {
      const lineas = [...new Set(this.rows.map(r => r.lineaOficial).filter(Boolean))].sort();
      return [{ text: 'Todas', value: '' }, ...lineas.map(l => ({ text: l, value: l }))];
    },
    // País y línea oficial por contrato, tomados de sus filas reales. Las filas
    // sintéticas en $0 se arman desde `contratos` y no traen esos campos; esto
    // se los presta en vez de recalcular acá la normalización que ya hizo el
    // backend, que sería una segunda definición de la misma regla.
    contratoMetaMap() {
      const map = {};
      for (const r of this.rows) {
        if (!r.contrato || map[r.contrato]) continue;
        if (r.pais || r.lineaOficial) {
          map[r.contrato] = { pais: r.pais ?? null, lineaOficial: r.lineaOficial ?? null };
        }
      }
      return map;
    },
    opcionesEstado() {
      return [
        { text: 'Activos', value: 'Activo' },
        { text: 'Inactivos', value: 'Inactivo' },
        { text: 'Todos', value: 'Todos' },
      ];
    },
    contratosVisibles() {
      return new Set(
        this.filteredRows.map(r => r.contrato).filter(Boolean)
      ).size;
    },
    contratosDisponibles() {
      const seen = new Set();
      return this.rows
        .filter(r => r.contrato && !seen.has(r.contrato) && seen.add(r.contrato))
        .map(r => ({
          text: [r.contrato, r.empresa, r.producto].filter(Boolean).join(' — '),
          value: r.contrato,
        }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    // codigo -> status ('Activo' | 'Churned' | 'Pausado'), para el filtro de
    // Estado y para completar los contratos sin fila de MRR ese mes.
    contratoStatusMap() {
      const map = {};
      this.contratos.forEach(c => { map[c.codigo] = c.status; });
      return map;
    },
    matchesEstado() {
      return (status) => {
        if (this.filtroEstado === 'Todos') return true;
        if (this.filtroEstado === 'Activo') return status === 'Activo';
        return status != null && status !== 'Activo';
      };
    },
    // Las filas de B2C landing no cuelgan de ningún contrato (contrato: null,
    // por diseño en mrr-detalle.service). Pasarlas por el filtro de Estado las
    // dejaba fuera siempre —status undefined nunca es 'Activo'— y su MRR
    // desaparecía del total, que así no cuadraba con MRR Resumen.
    //
    // Se muestran en Activos y en Todos, nunca en Inactivos: es ingreso
    // vigente, y listarlo bajo "Inactivos" sugería lo contrario. El total de
    // Activos sigue incluyéndolas, que es lo que hace cuadrar con MRR Resumen.
    matchesEstadoRow() {
      return (row) => {
        if (row.contrato == null) return this.filtroEstado !== 'Inactivo';
        return this.matchesEstado(this.contratoStatusMap[row.contrato]);
      };
    },
    filteredRows() {
      const base = this.rows
        .filter(r => !this.filtroAnio    || r.fecha?.startsWith(this.filtroAnio))
        .filter(r => !this.filtroMes     || r.fecha?.slice(5, 7) === this.filtroMes)
        .filter(r => !this.filtroContrato || r.contrato === this.filtroContrato)
        .filter(r => !this.filtroPais    || r.pais === this.filtroPais)
        .filter(r => !this.filtroLinea   || r.lineaOficial === this.filtroLinea)
        .filter(r => this.matchesEstadoRow(r));

      // Con año y mes específicos, un contrato sin fila de MRR ese mes
      // simplemente no aparecía — imposible comparar mes a mes sin saber si
      // bajó a 0 o si nunca existió esa fila. Se completan con un registro en
      // $0 para que todo contrato relevante aparezca siempre.
      if (!this.filtroAnio || !this.filtroMes) return base;

      const presentes = new Set(base.map(r => r.contrato));
      // Un contrato que aún no había empezado ese mes no es un "bajó a 0":
      // no existía. Se compara contra el último día posible del mes como
      // string YYYY-MM-DD para no depender de la zona horaria.
      const finMes = `${this.filtroAnio}-${this.filtroMes}-31`;
      const faltantes = this.contratos
        .filter(c => c.codigo && !presentes.has(c.codigo))
        .filter(c => !c.startDate || String(c.startDate).slice(0, 10) <= finMes)
        .filter(c => !this.filtroContrato || c.codigo === this.filtroContrato)
        .filter(c => this.matchesEstado(c.status))
        .map(c => {
          const meta = this.contratoMetaMap[c.codigo] ?? {};
          return {
            fecha: `${this.filtroAnio}-${this.filtroMes}-01`,
            contrato: c.codigo,
            empresa: c.company?.name ?? null,
            canal: c.canal && c.canal !== 'Directo' ? c.canal : null,
            producto: c.producto?.nombre ?? null,
            pais: meta.pais ?? c.company?.country?.name ?? null,
            lineaNegocio: c.lineaNegocio ?? null,
            lineaOficial: meta.lineaOficial ?? c.lineaNegocio ?? null,
            mrrLocal: 0,
            moneda: null,
            mrrUSD: 0,
            estimado: false,
            id: null,
            contratoId: c.id,
            companyId: c.companyId,
          };
        })
        // Los mismos filtros que las filas reales: un contrato en $0 de otro
        // país no debe colarse cuando se filtró por país.
        .filter(r => !this.filtroPais  || r.pais === this.filtroPais)
        .filter(r => !this.filtroLinea || r.lineaOficial === this.filtroLinea);

      // momUsd como campo propio (no derivado en el template) para que el
      // sort-by nativo de la tabla pueda ordenar numéricamente; sin variación
      // registrada se trata como 0 (sin cambio).
      return [...base, ...faltantes].map(r => ({
        ...r,
        momUsd: this.varFor(r.contrato)?.momUsd ?? 0,
      }));
    },
    totalUSD() {
      return this.filteredRows.reduce((sum, r) => sum + (r.mrrUSD || 0), 0);
    },
    // MRR más los extras del contrato (recaudación, comisión y gestión), en USD.
    // El backend solo calcula extras para el mes en curso, así que en meses pasados
    // `totalMrrUSD` viene igual a `mrrUSD` y este total coincide con el de arriba.
    totalMrrConExtrasUSD() {
      return this.filteredRows.reduce(
        (sum, r) => sum + (r.totalMrrUSD ?? r.mrrUSD ?? 0), 0);
    },
    // MRR del mes con la definición oficial: las mismas filas y el mismo
    // allow-list de (país, línea) que usan MRR Resumen y MRR Daily, ignorando
    // los filtros de la tabla. El backend marca cada fila con `oficial` y
    // `lineaOficial` (ver mrr-lineas.utils) para no reimplementar aquí ese
    // criterio y que las vistas vuelvan a divergir.
    //
    // Se redondea por (país, línea) antes de sumar, no al final, porque es lo
    // que hace MRR Resumen al mostrar cada línea: sumar los valores crudos
    // dejaba los dos totales separados por un par de dólares.
    //
    // Sin un mes concreto no hay un MRR del mes que mostrar (el MRR no se
    // acumula entre meses), así que con año o mes en "Todos" queda en null.
    mrrOficialMes() {
      if (!this.filtroAnio || !this.filtroMes) return null;
      const prefijo = `${this.filtroAnio}-${this.filtroMes}`;
      const porLinea = {};
      this.rows
        .filter(r => r.oficial && r.fecha?.startsWith(prefijo))
        .forEach(r => {
          const key = `${r.pais}||${r.lineaOficial}`;
          // Con extras, igual que el cálculo de metas y el snapshot diario: si acá se
          // sumara `mrrUSD` a secas, este número quedaría bajo el de Metas para la misma
          // línea y el mismo mes.
          porLinea[key] = (porLinea[key] || 0) + (r.totalMrrUSD ?? r.mrrUSD ?? 0);
        });
      return Object.values(porLinea).reduce((sum, v) => sum + Math.round(v), 0);
    },
    // Hay un mes único seleccionado (ni año ni mes en "Todos"), único caso en
    // que una serie diaria tiene a qué anclarse.
    periodoCompleto() {
      return Boolean(this.filtroAnio && this.filtroMes);
    },
    // Si el mes/año filtrado es el mes calendario actual — determina contra
    // qué fecha se piden las variaciones (ver loadVariations()).
    isCurrentMonth() {
      const now = new Date();
      return (
        Number(this.filtroAnio) === now.getFullYear() &&
        Number(this.filtroMes) === now.getMonth() + 1
      );
    },
  },
  watch: {
    filtroAnio() {
      this.loadVariations();
    },
    filtroMes() {
      this.loadVariations();
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    can,
    limpiarFiltros() {
      // El año en curso es el estado por defecto de la vista, no un filtro:
      // limpiar no debería dejar la tabla con todos los años de historia.
      this.filtroAnio = String(new Date().getFullYear());
      this.filtroMes = String(new Date().getMonth() + 1).padStart(2, '0');
      this.filtroPais = '';
      this.filtroLinea = '';
      this.filtroContrato = null;
      this.filtroEstado = 'Activo';
    },

    async loadData() {
      try {
        this.loadingData = true;
        this.loadingError = false;
        const res = await MRRService.getDetalleMRR();
        if (res.status === 'ok') {
          this.rows = res.data;
        } else {
          this.loadingError = true;
        }
        await this.loadVariations();
        // Todos los contratos (activos o no), para completar los que no
        // tengan fila de MRR el mes seleccionado y para el filtro de Estado.
        const contratosRes = await ContratoService.getAll();
        if (contratosRes.status === 'ok') {
          this.contratos = contratosRes.data;
        }
      } catch {
        this.loadingError = true;
      } finally {
        this.loadingData = false;
      }
    },

    // Variaciones por contrato (no bloquea la tabla si falla). Sin fecha, el
    // backend siempre compara contra hoy — por eso se le pasa una fecha
    // dentro del mes filtrado, para que Δ Mes refleje ESE mes y no siempre
    // "hoy vs. mes anterior". Con "Todos" (año o mes vacío) no hay un mes
    // único al cual anclar la comparación, así que se limpia y la tabla cae
    // a "—" (fallback ya existente en el template).
    async loadVariations() {
      if (!this.filtroAnio || !this.filtroMes) {
        this.variationsByContract = {};
        return;
      }
      const date = this.isCurrentMonth
        ? new Date().toISOString().slice(0, 10)
        : `${this.filtroAnio}-${this.filtroMes}-01`;
      const varRes = await MRRService.getMrrVariations(date);
      if (varRes.status === 'ok') {
        const map = {};
        (varRes.data.byContract || []).forEach((v) => {
          map[v.contrato] = v;
        });
        this.variationsByContract = map;
      } else {
        this.variationsByContract = {};
      }
    },

    varFor(contrato) {
      return contrato ? this.variationsByContract[contrato] || null : null;
    },

    openTrend(item) {
      if (!item.contrato || !this.periodoCompleto) return;
      this.trendContrato = item.contrato;
      this.trendDialog = true;
    },
    formatUSD(val) {
      if (val == null) return '—';
      return `USD ${Math.round(val).toLocaleString('en-US')}`;
    },
    formatLocal(val, moneda) {
      if (val == null) return '—';
      const rounded = Math.round(val);
      if (moneda === 'USD') return `$${rounded.toLocaleString('en-US')}`;
      return `$${rounded.toLocaleString('es-CL')} ${moneda || ''}`;
    },
    // Lo que suma el Total MRR por sobre el MRR del contrato, en el orden de la tabla anterior.
    extrasDetail(item) {
      return [
        { label: 'Recaudación', value: item.collectionFeeLocal },
        { label: 'Comisión',    value: item.commissionLocal },
        { label: 'Gestión',     value: item.managementFeeLocal },
        { label: 'Upsell',      value: item.upsellLocal },
      ];
    },
    hasExtras(item) {
      return this.extrasDetail(item).some(line => line.value != null);
    },
    rowClass(item) {
      return item.estimado ? 'orange lighten-5' : '';
    },
    lineaColor(linea) {
      const map = {
        'B2B Employee Benefit': 'blue darken-1',
        'B2B Asistencias':      'deep-purple darken-1',
        'B2C':                  'green darken-1',
      };
      return map[linea] || 'grey darken-1';
    },
    openEdit(item) {
      const [year, month] = (item.fecha || '').split('-');
      this.editItem = {
        id: item.id,
        mrrLocal: item.mrrLocal,
        moneda: item.moneda,
        mes: parseInt(month, 10) || 1,
        anio: parseInt(year, 10) || new Date().getFullYear(),
        notas: item.notas || null,
      };
      this.editError = false;
      this.editDialog = true;
      this.$nextTick(() => {
        if (this.$refs.editForm) this.$refs.editForm.validate();
      });
    },
    async saveEdit() {
      if (!this.$refs.editForm.validate()) return;
      this.editLoading = true;
      this.editError = false;
      try {
        const res = await MRRService.updateBilling(this.editItem.id, {
          priceBilling: this.editItem.mrrLocal,
          currency: this.editItem.moneda,
          monthBilling: this.editItem.mes,
          yearBilling: this.editItem.anio,
          notas: this.editItem.notas,
        });
        if (res.status === 'ok') {
          this.editDialog = false;
          await this.loadData();
        } else {
          this.editError = true;
        }
      } catch {
        this.editError = true;
      } finally {
        this.editLoading = false;
      }
    },
    // Filas sin fila de billing ese mes (ver filteredRows) — crea el registro
    // real vía el mismo endpoint que usa Registro Individual de Billing, en
    // vez de mandar a Carolina a otra pantalla para completar el dato.
    openCreate(item) {
      const [year, month] = (item.fecha || '').split('-');
      this.createItem = {
        contratoId: item.contratoId,
        companyId: item.companyId,
        contrato: item.contrato,
        empresa: item.empresa,
        mrrLocal: 0,
        moneda: item.moneda || 'CLP',
        mes: parseInt(month, 10) || 1,
        anio: parseInt(year, 10) || new Date().getFullYear(),
        notas: null,
      };
      this.createError = false;
      this.createDialog = true;
      this.$nextTick(() => {
        if (this.$refs.createForm) this.$refs.createForm.validate();
      });
    },
    async saveCreate() {
      if (!this.$refs.createForm.validate()) return;
      this.createLoading = true;
      this.createError = false;
      try {
        const res = await BillingService.createIndividual({
          companyId: this.createItem.companyId,
          contratoId: this.createItem.contratoId,
          priceBilling: this.createItem.mrrLocal,
          currency: this.createItem.moneda,
          monthBilling: this.createItem.mes,
          yearBilling: this.createItem.anio,
          notas: this.createItem.notas || undefined,
        });
        if (res.status === 'ok') {
          this.createDialog = false;
          await this.loadData();
        } else {
          this.createError = true;
        }
      } catch {
        this.createError = true;
      } finally {
        this.createLoading = false;
      }
    },
    isB2B2C(item) {
      return item.contrato && (
        (item.lineaNegocio && item.lineaNegocio.startsWith('B2B2C')) ||
        item.lineaNegocio === 'B2C'
      );
    },
    /** Las membresías de un contrato (con email) requieren `mrr-breakdown:view-memberships`. */
    canOpenBreakdown(item) {
      return this.isB2B2C(item) && can('mrr-breakdown:view-memberships');
    },
    async openBreakdown(item) {
      this.breakdownContrato = item.contrato;
      this.breakdownEmpresa = item.empresa;
      this.breakdownRows = [];
      this.breakdownLoading = true;
      this.breakdownDialog = true;
      try {
        const res = await MRRService.getB2B2CBreakdown(item.contrato);
        if (res.status === 'ok') this.breakdownRows = res.data;
      } finally {
        this.breakdownLoading = false;
      }
    },
    openDelete(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      if (!this.deleteTarget?.id) return;
      this.deleteLoading = true;
      try {
        const res = await MRRService.deleteBilling(this.deleteTarget.id);
        if (res.status === 'ok') {
          this.rows = this.rows.filter(r => r.id !== this.deleteTarget.id);
          this.deleteDialog = false;
          this.deleteTarget = null;
        } else {
          this.deleteError = true;
          this.deleteDialog = false;
        }
      } catch {
        this.deleteError = true;
        this.deleteDialog = false;
      } finally {
        this.deleteLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Celdas un poco más angostas que las de Vuetify (16px por lado) para que las 12
   columnas quepan sin scroll horizontal en una pantalla de notebook. */
.mrr-breakdown-table >>> th,
.mrr-breakdown-table >>> td {
  padding: 0 10px !important;
}

.extras-line {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 2px 0;
  white-space: nowrap;
}
</style>
